import { logger } from "@scott/logger";
import type {
  SliderSlide,
  SliderSlideMedia,
} from "@/scott/ui/blocks/slider/slider";
import { SLIDER_MAX_SLIDES } from "@/scott/ui/blocks/slider/slider.constants";
import { adaptButtons } from "@/services/contentful/assemblies/page/adapter/button-adapter";
import {
  CLOUDINARY_TRANSFORMATION_D2C_TALL,
  CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  extractCloudinaryAssetUrl,
  extractMediaCreativeName,
  getBlockId,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";
import type { SectionHeroSlideInput } from "@/services/contentful/components/SectionHero/section-hero.adapter";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionSliderPageBlock } from "@/types/page-block";

// biome-ignore lint/style/noMagicNumbers: srcset candidate widths
const SLIDER_SRCSET_WIDTHS = [640, 1024, 1536, 1920, 2560];
const SLIDER_DEFAULT_WIDTH = 1536;
const DEFAULT_AUTOPLAY_DURATION_SECONDS = 5;
const MIN_AUTOPLAY_DURATION_SECONDS = 2;
const MAX_AUTOPLAY_DURATION_SECONDS = 30;
const MILLISECONDS_PER_SECOND = 1000;

type SliderMediaItem = NonNullable<
  NonNullable<
    NonNullable<SectionHeroSlideInput["mediaCollection"]>["items"]
  >[number]
>;
type SliderMediaImage = Extract<SliderMediaItem, { __typename: "MediaImage" }>;
type SliderMediaVideo = Extract<SliderMediaItem, { __typename: "MediaVideo" }>;

export interface SectionSliderInput {
  sys?: { id: string };
  __typename: "SectionSlider";
  internalName: string;
  autoplay?: boolean | null;
  autoplayDuration?: number | null;
  fullWidth?: boolean | null;
  uiConfiguration?: UiConfigurationInput | null;
  slidesCollection?: {
    items?: Array<SectionHeroSlideInput | null> | null;
  } | null;
}

function sliderSrcSet(src: string, transform: string | string[]) {
  return getCloudinarySrcSet(src, {
    transform,
    widths: SLIDER_SRCSET_WIDTHS,
    defaultWidth: SLIDER_DEFAULT_WIDTH,
  });
}

function extractImage(
  mediaItem: SliderMediaImage
): SliderSlideMedia | undefined {
  const src = extractCloudinaryAssetUrl(mediaItem.image);

  if (!src) {
    return;
  }

  const hasFocalPoint = Boolean(mediaItem.hasFocalPoint);
  const desktop = sliderSrcSet(
    src,
    resolveTransform(CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN, hasFocalPoint)
  );
  const mobile = sliderSrcSet(
    src,
    resolveTransform(CLOUDINARY_TRANSFORMATION_D2C_TALL, hasFocalPoint)
  );

  return {
    sys: mediaItem.sys,
    src: desktop?.src ?? src,
    srcSet: desktop?.srcSet,
    mobileSrc: mobile?.src,
    mobileSrcSet: mobile?.srcSet,
    alt: mediaItem.alternativeText ?? "",
    type: "Image",
  };
}

function extractVideo(
  mediaItem: SliderMediaVideo
): SliderSlideMedia | undefined {
  const src =
    extractCloudinaryAssetUrl(mediaItem.video) ||
    mediaItem.externalVideoUrl ||
    "";
  const poster = extractCloudinaryAssetUrl(mediaItem.poster);

  // An inactive video slide only shows its poster, so one is mandatory here.
  if (!(src && poster)) {
    return;
  }

  return {
    sys: mediaItem.sys,
    src,
    poster,
    type: "Video",
  };
}

/** Resolves the first usable image or video of a linked Hero Section. */
function extractSlideMedia(
  mediaCollection: SectionHeroSlideInput["mediaCollection"]
): SliderSlideMedia | undefined {
  const items = mediaCollection?.items ?? [];

  for (const mediaItem of items) {
    if (!mediaItem) {
      continue;
    }

    const media =
      mediaItem.__typename === "MediaImage"
        ? extractImage(mediaItem)
        : extractVideo(mediaItem);

    if (media) {
      return media;
    }
  }
}

function normalizeAutoplayDelay(duration?: number | null): number {
  const roundedDuration = Math.round(
    duration ?? DEFAULT_AUTOPLAY_DURATION_SECONDS
  );
  const durationSeconds = Math.min(
    MAX_AUTOPLAY_DURATION_SECONDS,
    Math.max(MIN_AUTOPLAY_DURATION_SECONDS, roundedDuration)
  );

  return durationSeconds * MILLISECONDS_PER_SECOND;
}

function allocateSlideId(
  contentfulId: string | undefined,
  slideIndex: number,
  rootBlockId: string,
  usedIds: Set<string>,
  reservedContentfulIds: ReadonlySet<string>
): string {
  if (contentfulId && !usedIds.has(contentfulId)) {
    usedIds.add(contentfulId);
    return contentfulId;
  }

  const fallbackBase = `${rootBlockId}-slide-${slideIndex}`;
  let fallbackId = fallbackBase;
  let disambiguator = 1;

  while (usedIds.has(fallbackId) || reservedContentfulIds.has(fallbackId)) {
    fallbackId = `${fallbackBase}-${disambiguator}`;
    disambiguator += 1;
  }

  usedIds.add(fallbackId);
  return fallbackId;
}

function toSliderSlide(
  hero: SectionHeroSlideInput,
  media: SliderSlideMedia,
  id: string,
  basePath?: string
): SliderSlide {
  return {
    sys: hero.sys,
    id,
    internalName: hero.internalName,
    creativeName: extractMediaCreativeName(hero.mediaCollection?.items),
    media,
    title: hero.content,
    buttons: adaptButtons(hero.ctaCollection?.items, basePath),
    color: hero.textColor,
    textHorizontalPosition: hero.textHorizontalPosition,
    textVerticalPosition: hero.textVerticalPosition,
  };
}

/**
 * Adapts a SectionSlider and its ordered Hero references. A linked Hero only
 * contributes its content — headline, copy, calls to action, text colour and
 * placement — plus its first usable medium. Hero-only presentation settings
 * (hero layout, SVG headline, presentation, autoplay) belong to the Hero block
 * and are deliberately ignored here.
 */
export function SectionSliderAdapter(
  item: SectionSliderInput,
  index: number,
  basePath?: string
): SectionSliderPageBlock {
  const rootBlockId = getBlockId(item, index);
  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;
  const rawSlides = (item.slidesCollection?.items ?? []).slice(
    0,
    SLIDER_MAX_SLIDES
  );
  const reservedContentfulIds = new Set(
    rawSlides.flatMap((slide) => (slide?.sys?.id ? [slide.sys.id] : []))
  );
  const usedSlideIds = new Set<string>();

  const slides = rawSlides.flatMap((slide, slideIndex) => {
    if (!slide) {
      return [];
    }

    const media = extractSlideMedia(slide.mediaCollection);

    if (!media) {
      logger.warn(
        "SectionSliderAdapter: skipping a Hero without compatible media or a video poster",
        { blockId: rootBlockId, slideId: slide.sys?.id }
      );
      return [];
    }

    const id = allocateSlideId(
      slide.sys?.id,
      slideIndex,
      rootBlockId,
      usedSlideIds,
      reservedContentfulIds
    );

    return [toSliderSlide(slide, media, id, basePath)];
  });

  if (slides.length < 2) {
    logger.warn(
      "SectionSliderAdapter: fewer than two compatible Hero slides resolved",
      { blockId: rootBlockId, slideCount: slides.length }
    );
  }

  return {
    sys: item.sys,
    id: rootBlockId,
    __typename: item.__typename,
    internalName: item.internalName,
    slides,
    autoplay: item.autoplay ?? false,
    autoplayDelay: normalizeAutoplayDelay(item.autoplayDuration),
    fullWidth: item.fullWidth ?? false,
    backgroundColor: uiConfig?.backgroundColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
